// レイヤーサムネイル。表面の revision ごとにキャッシュし、生成は非同期 (createImageBitmap の縮小)。
import { ref } from 'vue'
import { pixelStore } from '@/pixels/pixelStore'

const THUMB = 40
const cache = new Map<string, { revision: number; url: string }>()
const pending = new Set<string>()
/** 更新通知用 */
export const thumbVersion = ref(0)

export function thumbnailUrl(pixelId: string): string | null {
  const s = pixelStore.get(pixelId)
  if (!s) return null
  const c = cache.get(pixelId)
  if (c && c.revision === s.revision) return c.url
  if (!pending.has(pixelId)) {
    pending.add(pixelId)
    void generate(pixelId, s.revision).finally(() => pending.delete(pixelId))
  }
  return c?.url ?? null
}

async function generate(pixelId: string, revision: number) {
  const s = pixelStore.get(pixelId)
  if (!s || s.revision !== revision) return
  const b = s.contentBounds()
  if (!b) {
    cache.set(pixelId, { revision, url: '' })
    thumbVersion.value++
    return
  }
  // 大きな表面は間引いて読む
  const step = Math.max(1, Math.floor(Math.max(b.w, b.h) / 256))
  const w = Math.max(1, Math.floor(b.w / step))
  const h = Math.max(1, Math.floor(b.h / step))
  const full = s.readRect(b)
  const c = s.channels
  const rgba = new Uint8ClampedArray(w * h * 4)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const si = ((y * step) * b.w + x * step) * c
      const di = (y * w + x) * 4
      if (c === 4) {
        rgba[di] = full[si]!; rgba[di + 1] = full[si + 1]!; rgba[di + 2] = full[si + 2]!; rgba[di + 3] = full[si + 3]!
      } else {
        const v = full[si]!
        rgba[di] = v; rgba[di + 1] = v; rgba[di + 2] = v; rgba[di + 3] = 255
      }
    }
  }
  const scale = Math.min(THUMB / w, THUMB / h, 1)
  const tw = Math.max(1, Math.round(w * scale))
  const th = Math.max(1, Math.round(h * scale))
  const bitmap = await createImageBitmap(new ImageData(rgba, w, h), { resizeWidth: tw, resizeHeight: th, resizeQuality: 'medium' })
  const canvas = new OffscreenCanvas(tw, th)
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0)
  bitmap.close()
  const blob = await canvas.convertToBlob({ type: 'image/png' })
  const old = cache.get(pixelId)
  if (old?.url) URL.revokeObjectURL(old.url)
  cache.set(pixelId, { revision, url: URL.createObjectURL(blob) })
  thumbVersion.value++
}

/** 使われなくなった表面のサムネイルを捨てる */
export function retainThumbnails(ids: Set<string>): void {
  for (const [id, c] of [...cache]) {
    if (!ids.has(id)) {
      if (c.url) URL.revokeObjectURL(c.url)
      cache.delete(id)
    }
  }
}
